"use client";

import { useId, useState } from "react";
import { MessageCircleMore } from "lucide-react";
import { cn } from "@/lib/utils";

const MOM_TEST_QUESTIONS = [
  {
    id: "q-problem-definition",
    question:
      "Do users describe a concrete, specific problem they actively face?",
    hint: "Look for precise pain descriptions, not vague opinions.",
  },
  {
    id: "q-customer-contact",
    question:
      "Have you seen or heard at least 5 people complaining about this problem?",
    hint: "Indirect signals or third-party reports don't count.",
  },
  {
    id: "q-payment-evidence",
    question:
      "Are people spending money or meaningful time to solve or work around this?",
    hint: "Paid tools, subscriptions, or time invested in workarounds count.",
  },
  {
    id: "q-currently-solving",
    question: "Are users actively working to solve this problem today?",
    hint: "Look for active pursuit, not passive interest.",
  },
  {
    id: "q-recommend",
    question: "Would you recommend a solution to a friend in this situation?",
    hint: "If you wouldn't recommend it, the pain may not be urgent enough.",
  },
] as const;

export interface MomTestAnswerMap {
  [key: string]: string;
}

export interface MomTestSectionProps {
  answers: MomTestAnswerMap;
  onAnswer: (questionId: string, answer: string) => void;
}

export function MomTestSection({ answers, onAnswer }: MomTestSectionProps) {
  const [expandedHint, setExpandedHint] = useState<string | null>(null);
  const inputId = useId();

  return (
    <section className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-center gap-2">
        <MessageCircleMore
          className="size-3.5 text-primary-foreground/40"
          aria-hidden="true"
        />
        <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/50">
          Mom Test validation
        </p>
      </div>
      <p className="font-mono text-[12px] leading-[1.8] text-primary-foreground/62 sm:text-[13px]">
        Ask real questions to real people. If the answers feel rehearsed or
        hypothetical, dig deeper.
      </p>
      <p className="font-mono text-[12px] leading-[1.7] text-primary-foreground/50 sm:text-[13px]">
        Treat these prompts as a founder checklist: the stronger your answers,
        the more trustworthy the validation score becomes.
      </p>
      <ul className="space-y-4">
        {MOM_TEST_QUESTIONS.map((item) => {
          const answer = answers[item.id] ?? "";
          const hintId = `${item.id}-hint-${inputId}`;
          const isExpanded = expandedHint === item.id;

          return (
            <li key={item.id} className="space-y-1.5">
              <label
                htmlFor={`${item.id}-${inputId}`}
                className="font-mono text-[12px] leading-[1.7] text-primary-foreground/82 sm:text-[13px]"
              >
                {item.question}
              </label>
              <div className="flex items-center gap-2">
                <textarea
                  id={`${item.id}-${inputId}`}
                  aria-describedby={isExpanded ? hintId : undefined}
                  rows={2}
                  value={answer}
                  onChange={(e) => onAnswer(item.id, e.target.value)}
                  placeholder="Your answer..."
                  className={cn(
                    "flex-1 resize-y rounded-lg border bg-black/15 px-3 py-2.5 font-mono text-[12px] leading-[1.8] text-primary-foreground placeholder:text-primary-foreground/30 sm:text-[13px]",
                    "border-white/15 focus:border-accent/30 focus:outline-none focus:ring-1 focus:ring-accent/15",
                  )}
                />
                <button
                  type="button"
                  onClick={() => setExpandedHint(isExpanded ? null : item.id)}
                  className="shrink-0 font-mono text-[11px] text-primary-foreground/48 transition-colors hover:text-primary-foreground/72"
                  aria-expanded={isExpanded}
                  aria-controls={hintId}
                >
                  {isExpanded ? "Hide hint" : "Hint"}
                </button>
              </div>
              {isExpanded && (
                <p
                  id={hintId}
                  className="font-mono text-[11px] leading-[1.7] text-primary-foreground/52"
                >
                  {item.hint}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
